<template>
  <a-modal
    :visible="visible"
    :title="modalTitle"
    :footer="null"
    @cancel="handleCancel"
    :closable="true"
    :maskClosable="true"
    width="800px"
  >
    <a-form-model
      ref="repaymentForm"
      :model="formState"
      :rules="formRules"
    >
      <div style="padding: 20px 0">
        <a-row :gutter="24">
          <!-- Top Row -->
          <a-col :span="12">
            <a-form-model-item
              label="Invoice #"
              prop="invoiceId"
              style="margin-bottom: 16px"
            >
              <a-select
                v-model="formState.invoiceId"
                placeholder="Select invoice"
                style="width: 100%"
                @change="onInvoiceChange"
                :loading="invoicesLoading"
                :disabled="!!preSelectedInvoice"
                show-search
                :filter-option="false"
                :not-found-content="
                  invoicesLoading ? 'Loading...' : 'No invoices found'
                "
              >
                <a-select-option
                  v-for="invoice in eligibleInvoices"
                  :key="invoice.invoiceId"
                  :value="invoice.invoiceId"
                >
                  {{ invoice.invoiceId }}
                </a-select-option>
              </a-select>
            </a-form-model-item>
          </a-col>

          <a-col :span="12">
            <a-form-model-item
              label="Reference/Transaction ID"
              prop="referenceId"
              style="margin-bottom: 16px"
            >
              <a-input
                v-model="formState.referenceId"
                placeholder="e.g. TRX123456789"
                style="width: 100%"
              />
            </a-form-model-item>
          </a-col>
        </a-row>

        <a-row :gutter="24">
          <!-- Bottom Row -->
          <a-col :span="12">
            <a-form-model-item
              label="Payment Date"
              prop="paymentDate"
              required
              style="margin-bottom: 16px"
            >
              <a-date-picker
                v-model="formState.paymentDate"
                placeholder="Select payment date"
                style="width: 100%"
                format="YYYY-MM-DD"
              />
            </a-form-model-item>
          </a-col>

          <a-col :span="12">
            <a-form-model-item
              label="Amount"
              prop="amount"
              required
              style="margin-bottom: 16px"
            >
              <a-input-number
                v-model="formState.amount"
                placeholder="Enter amount"
                style="width: 100%"
                :min="0"
                :max="
                  selectedInvoiceData
                    ? selectedInvoiceData.outstanding
                    : undefined
                "
                :precision="2"
                prefix="$"
              />
              <!-- Show outstanding amount if invoice is selected -->
              <div v-if="formState.invoiceId && selectedInvoiceData">
                <span style="font-size: 12px; color: #666">
                  Outstanding Amount:
                  <strong style="color: #26a246">{{
                    formatAmount(selectedInvoiceData.outstanding)
                  }}</strong>
                </span>
              </div>
            </a-form-model-item>
          </a-col>
        </a-row>

        <a-row :gutter="24">
          <!-- Payment Method Row -->
          <a-col :span="12">
            <a-form-model-item
              label="Payment Method"
              prop="paymentMethod"
              required
              style="margin-bottom: 16px"
            >
              <a-select
                v-model="formState.paymentMethod"
                placeholder="Select payment method"
                style="width: 100%"
              >
                <a-select-option value="Bank Transfer"
                  >Bank Transfer</a-select-option
                >
                <a-select-option value="Mobile Money"
                  >Mobile Money</a-select-option
                >
                <a-select-option value="Cash">Cash</a-select-option>
              </a-select>
            </a-form-model-item>
          </a-col>
          <a-col :span="12">
            <!-- Empty column for spacing -->
          </a-col>
        </a-row>

        <!-- Notes Section - Full Width -->
        <a-form-model-item
          label="Notes (Optional)"
          prop="notes"
          style="margin-bottom: 16px"
        >
          <a-textarea
            v-model="formState.notes"
            placeholder="Add any additional notes about this repayment"
            :rows="4"
          />
        </a-form-model-item>

        <!-- File Upload Section - Full Width -->
        <a-form-model-item
          label="Payment Proof"
          prop="paymentProof"
          required
          style="margin-bottom: 16px"
        >
          <a-upload
            :file-list="formState.paymentProof"
            :remove="handleRemove"
            accept=".jpg,.jpeg,.png,.pdf"
            list-type="text"
            @change="handleFileChange"
          >
            <a-button>
              <a-icon type="upload" />
              Upload File
            </a-button>
            <div
              slot="tip"
              style="color: #666; font-size: 12px; margin-top: 8px"
            >
              Supported formats: JPEG, PNG, PDF (Max file size: 5MB)
            </div>
          </a-upload>
        </a-form-model-item>

        <!-- Information Box -->
        <div
          style="
            background: #f0f9ff;
            border: 1px solid #bae6fd;
            border-radius: 6px;
            padding: 16px;
            margin-top: 24px;
            display: flex;
            align-items: flex-start;
            gap: 12px;
          "
        >
          <a-icon type="info-circle" style="color: #0ea5e9; margin-top: 2px" />
          <div>
            <div style="font-weight: 600; color: #0c4a6e; margin-bottom: 4px">
              About Repayment Requests
            </div>
            <div style="color: #0c4a6e; font-size: 14px">
              Repayment requests are used to track and manage payments owed by
              buyers. These will be reflected in the buyer's account immediately
              after saving.
            </div>
          </div>
        </div>

        <!-- Action Buttons -->
        <div
          style="
            display: flex;
            gap: 12px;
            justify-content: flex-end;
            margin-top: 24px;
          "
        >
          <a-button @click="handleCancel"> Cancel </a-button>
          <a-button
            type="primary"
            :loading="createRepaymentLoading"
            @click="handleSubmit"
            style="background: #26a246; border-color: #26a246"
          >
            Save Repayment
          </a-button>
        </div>
      </div>
    </a-form-model>
  </a-modal>
</template>

<script>
import FinancingAndPaymentsService from "../../api/financing-and-payments.service";
import SellersService from "../../api/sellers.service";
import { mapGetters } from "vuex";
import { formatAmount } from "../../utils";

export default {
  name: "CreateRepaymentModal",
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    preSelectedInvoice: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      createRepaymentLoading: false,
      formState: {
        invoiceId: undefined,
        referenceId: "",
        paymentDate: undefined,
        amount: undefined,
        paymentMethod: undefined,
        notes: "",
        paymentProof: [],
      },
      eligibleInvoices: [],
      invoicesLoading: false,
      selectedInvoiceData: null,
    };
  },
  computed: {
    ...mapGetters(["IsAgentProfileAvailable"]),
    modalTitle() {
      if (this.preSelectedInvoice && this.preSelectedInvoice.invoiceIdFormatted) {
        return `Record Repayment - ${this.preSelectedInvoice.invoiceIdFormatted}`;
      }
      return "Record Repayment";
    },
    formRules() {
      return {
        invoiceId: [{ required: true, message: 'Please select an invoice', trigger: 'change' }],
        referenceId: [{ required: true, message: 'Please enter a reference/transaction ID', trigger: 'blur' }],
        paymentDate: [{ required: true, message: 'Please select a payment date', trigger: 'change' }],
        amount: [
          {
            required: true,
            message: "Please enter the amount due",
            trigger: 'blur'
          },
          {
            validator: (rule, value, callback) => {
              if (!value || value <= 0) {
                callback(new Error("Amount must be greater than 0"));
                return;
              }
              if (
                this.selectedInvoiceData &&
                value > this.selectedInvoiceData.outstanding
              ) {
                callback(new Error(
                  `Amount cannot exceed the outstanding amount of ${this.formatAmount(
                    this.selectedInvoiceData.outstanding
                  )}`
                ));
                return;
              }
              callback();
            },
            trigger: 'blur'
          },
        ],
        paymentMethod: [{ required: true, message: 'Please select a payment method', trigger: 'change' }],
        paymentProof: [{ required: true, message: 'Please upload payment proof', trigger: 'change' }],
      };
    },
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.initializeForm();
        
        // If we have a pre-selected invoice, use it directly instead of fetching all eligible invoices
        if (this.preSelectedInvoice) {
          // Transform the invoice data to match the expected structure from the API
          const transformedInvoice = {
            invoiceId: this.preSelectedInvoice.invoiceId,
            invoiceIdFormatted: this.preSelectedInvoice.invoiceIdFormatted,
            outstanding: this.preSelectedInvoice.outstanding,
            factoredValue: this.preSelectedInvoice.factoredValue,
            buyerName: this.preSelectedInvoice.buyerName,
            // Add any other properties that might be needed
            ...this.preSelectedInvoice
          };
          
          this.eligibleInvoices = [transformedInvoice]; // Set as single invoice in array
          this.formState.invoiceId = this.preSelectedInvoice.invoiceId;
          this.onInvoiceChange(this.preSelectedInvoice.invoiceId);
        } else {
          // Only fetch eligible invoices if no pre-selected invoice
          this.fetchEligibleInvoices();
        }
      }
    },
  },
  methods: {
    formatAmount,
    initializeForm() {
      this.resetNewRepaymentForm();
    },
    handleCancel() {
      this.$emit("cancel");
    },
    resetNewRepaymentForm() {
      this.formState = {
        invoiceId: undefined,
        referenceId: "",
        paymentDate: undefined,
        amount: undefined,
        paymentMethod: undefined,
        notes: "",
        paymentProof: [],
      };
      this.selectedInvoiceData = null;
    },
    async fetchEligibleInvoices() {
      this.invoicesLoading = true;

      const response =
        await FinancingAndPaymentsService.getEligibleInvoicesForRepayment({
          EligibleForRepayments: true,
        });
      console.log("Fetched invoices response:", response);
      if (response.data?.code === 200) {
        this.eligibleInvoices = response.data.data.results || [];
        console.log("Eligible invoices:", this.eligibleInvoices);
      } else {
        this.eligibleInvoices = [];
        console.log("No invoices found or error:", response.data);
        this.$message.warning(
          response.data?.message ||
            "No eligible invoices received from the server"
        );
      }

      this.invoicesLoading = false;
    },
    onInvoiceChange(invoiceId) {
      // Find the selected invoice and populate amount
      const selectedInvoice = this.eligibleInvoices.find(
        (invoice) => invoice.invoiceId === invoiceId
      );
      console.log("Selected invoice data:", selectedInvoice);
      if (selectedInvoice) {
        this.selectedInvoiceData = selectedInvoice;
      } else {
        this.selectedInvoiceData = null;
        this.formState.amount = null;
      }
    },
    handleSubmit() {
      console.log('handleSubmit called');
      this.$refs.repaymentForm.validate(valid => {
        if (valid) {
          this.saveRepayment();
        } else {
          return false;
        }
      });
    },
    async saveRepayment() {
      console.log('saveRepayment called!');
      this.createRepaymentLoading = true;
      let repaymentData = this.formState;

      // Step 1: Upload payment proof file first
      console.log("Uploading payment proof file...");
      const uploadResponse = await SellersService.uploadMultipleFiles(
        this.formState.paymentProof.map((file) => file.originFileObj || file)
      );

      if (uploadResponse.data?.code === 200) {
        // Files uploaded successfully, get the URLs
        const fileUrls = uploadResponse.data.data[0].url;
        console.log("Files uploaded successfully:", fileUrls);

        // Step 2: Create repayment with file URLs
        repaymentData = {
          ...repaymentData,
          paymentProofUrls: fileUrls,
        };

        console.log("Saving repayment with payment proof:", repaymentData);
      } else {
        // File upload failed - return immediately, cannot proceed without payment proof
        this.$message.error(
          uploadResponse.data?.message ||
            "Failed to upload payment proof files. Please try again."
        );
        return; // Exit early, don't proceed to repayment creation
      }

      // Step 3: Call the API to record the repayment
      const repaymentPayload = {
        invoiceId: repaymentData.invoiceId,
        amount: parseFloat(repaymentData.amount),
        paymentMethod: repaymentData.paymentMethod,
        repaymentDate: new Date(repaymentData.paymentDate).toISOString(),
        referenceTransactionId: repaymentData.referenceId,
        notes: repaymentData.notes || "",
        attachments: [repaymentData.paymentProofUrls],
        recordedBy: this.IsAgentProfileAvailable?.name || "",
      };

      console.log(
        "Calling recordRepayment API with payload:",
        repaymentPayload
      );
      const response = await FinancingAndPaymentsService.recordRepayment(
        repaymentPayload
      );

      if (response.data?.code === 200) {
        this.$message.success("Repayment recorded successfully!");
        this.$emit("success");
        this.handleCancel();
      } else {
        this.$message.error(
          response.data?.message ||
            "Failed to record repayment. Please try again."
        );
      }

      this.createRepaymentLoading = false;
    },

    handleFileChange(info) {
      const { file, fileList } = info;
      console.log("File change:", file, fileList);

      // Update paymentProof with the fileList
      this.formState.paymentProof = fileList;

      // Validate the file if it's being added
      if (file.status === "uploading") {
        // Validate file type
        const isValidType = [
          "image/jpeg",
          "image/jpg",
          "image/png",
          "application/pdf",
        ].includes(file.type);

        if (!isValidType) {
          this.$message.error("You can only upload JPEG, PNG, or PDF files!");
          return;
        }

        // Validate file size (5MB limit)
        const isLt5M = file.size / 1024 / 1024 < 5;
        if (!isLt5M) {
          this.$message.error("File size must be smaller than 5MB!");
          return;
        }
      }
    },

    handleRemove(file) {
      console.log("Removing file:", file);
      // The fileList will be automatically updated by Ant Design
      return true;
    },
  },
};
</script>

<style scoped>
/* Add any component-specific styles here */
</style>
